/**
 * Hook agregador da Caixa de Pendências.
 *
 * Junta itens de seis fontes que hoje vivem em telas separadas:
 *   1. Aprovações de agendamento (appointments PENDENTE)
 *   2. Solicitações de lente vindas do portal (lensOrderRequests PENDING)
 *   3. Pedidos de lente em andamento (lensOrders CONFIRMED, aguardando entrega)
 *   4. Alertas de estoque baixo (inventory abaixo do mínimo)
 *   5. Procedimentos solicitados em atraso (SURGERY/EXAM_REQUEST com
 *      executionStatus=PENDING criados há mais que o prazo configurado)
 *   6. Atendimentos finalizados aguardando orientação à paciente
 *      (appointments CONCLUIDA com secretaryBriefing.briefed=false)
 *   7. Pagamentos em aberto (cobrança PENDENTE/PARCIAL/CONVENIO de dia
 *      que já acabou — ver utils/receivables.ts)
 *
 * O filtro por papel é aplicado aqui — a tela só recebe o que aquele papel
 * deveria ver. Quando o usuário não tem permissão para uma fonte, a fonte
 * volta vazia e o contador fica 0 (sem flickar erro).
 *
 * Agendamentos PENDENTE e briefings pendentes usam onSnapshot porque
 * são as filas mais sensíveis a tempo real (paciente acaba de pedir
 * / médico acaba de finalizar). As outras fontes são carregadas com
 * getDocs e re-buscadas via `refresh()` — está bom o suficiente pro
 * volume típico e evita custo desnecessário de listeners.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/services/firebase/config';
import { LensOrderRequestService } from '@/services/firebase/lensOrderRequests';
import { LensOrderService } from '@/services/firebase/lensOrders';
import { InventoryService, type StockAlert } from '@/services/firebase/inventory';
import { PrescriptionService } from '@/services/firebase/prescriptions';
import { PatientService } from '@/services/firebase/patients';
import {
  OverdueDeadlinesService,
  daysSince,
  type OverdueDeadlinesConfig,
} from '@/services/firebase/overdueProcedures';
import { SecretaryBriefingService } from '@/services/firebase/secretaryBriefing';
import { ReceivableService } from '@/services/firebase/receivables';
import { isFinancialPendencia } from '@/utils/receivables';
import { Role } from '@/types/auth';
import type { Appointment, Patient, Prescription } from '@/types/models';
import type { LensOrder } from '@/types/lc';
import type { LensOrderRequest } from '@/types/lc';

export type PendenciaCategory =
  | 'APPOINTMENT_APPROVAL'
  | 'LENS_REQUEST'
  | 'LENS_ORDER_IN_PROGRESS'
  | 'STOCK_LOW'
  | 'PROCEDURE_OVERDUE'
  | 'BRIEFING_NEEDED'
  | 'PORTAL_CANCELLATION'
  | 'PORTAL_NEW_PATIENT'
  | 'FINANCIAL_OPEN';

/**
 * Item resolvido para a UI de "Procedimentos em atraso": casa a prescription
 * (que tem patientId mas não tem nome do paciente) com o nome para listar
 * sem segundo fetch na tela.
 */
export interface OverdueProcedureItem {
  prescription: Prescription;
  patientId: string;
  patientName: string;
  daysWaiting: number;
  thresholdDays: number;
}

export interface PendenciasState {
  loading: boolean;
  error: string | null;
  appointments: Appointment[];
  lensRequests: LensOrderRequest[];
  lensOrdersInProgress: LensOrder[];
  stockAlerts: StockAlert[];
  overdueProcedures: OverdueProcedureItem[];
  briefingsNeeded: Appointment[];
  portalCancellations: Appointment[];
  newPortalPatients: Patient[];
  /** TODAS as cobranças com dívida em aberto (inclusive de hoje/futuro).
   *  A fila conta só as `isFinancialPendencia`; a tela usa o resto pra
   *  mostrar o que ainda está "no dia". */
  openReceivables: Appointment[];
  financialPendencias: Appointment[];
  refresh: () => Promise<void>;
  total: number;
}

const ROLE_ACCESS: Record<PendenciaCategory, Role[]> = {
  APPOINTMENT_APPROVAL: [Role.ADMIN, Role.SECRETARIA],
  LENS_REQUEST: [Role.ADMIN, Role.MEDICO],
  LENS_ORDER_IN_PROGRESS: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA],
  STOCK_LOW: [Role.ADMIN, Role.FINANCEIRO, Role.SECRETARIA],
  PROCEDURE_OVERDUE: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA],
  BRIEFING_NEEDED: [Role.ADMIN, Role.SECRETARIA, Role.RECEPCIONISTA],
  PORTAL_CANCELLATION: [Role.ADMIN, Role.SECRETARIA, Role.RECEPCIONISTA],
  // Exatamente os três papéis que firestore.rules deixa dar update em
  // `patients`. RECEPCIONISTA fica de fora de propósito: a ação "Conferido"
  // grava no doc do paciente, e pra ela a regra nega — o botão apareceria e
  // falharia calado (o catch do handler só faz console.error).
  PORTAL_NEW_PATIENT: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA],
  // Quem mexe em cobrança. FINANCEIRO dá baixa por uma regra própria em
  // firestore.rules (só campos de pagamento).
  FINANCIAL_OPEN: [Role.ADMIN, Role.MEDICO, Role.SECRETARIA, Role.RECEPCIONISTA, Role.FINANCEIRO],
};

export function canSee(role: Role | undefined, category: PendenciaCategory): boolean {
  if (!role) return false;
  return ROLE_ACCESS[category].includes(role);
}

export function usePendencias(clinicId: string | undefined, role: Role | undefined): PendenciasState {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [lensRequests, setLensRequests] = useState<LensOrderRequest[]>([]);
  const [lensOrdersInProgress, setLensOrdersInProgress] = useState<LensOrder[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [overdueProcedures, setOverdueProcedures] = useState<OverdueProcedureItem[]>([]);
  const [briefingsNeeded, setBriefingsNeeded] = useState<Appointment[]>([]);
  const [portalCancellations, setPortalCancellations] = useState<Appointment[]>([]);
  const [newPortalPatients, setNewPortalPatients] = useState<Patient[]>([]);
  const [openReceivables, setOpenReceivables] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Cruza prescrições PENDING com a configuração de prazos e resolve o
   * nome do paciente em batch. Documentos pré-feature (sem
   * `executionStatus`) ficam de fora — `listPendingProcedures` filtra no
   * Firestore, e o caso é intencional pra não entulhar o painel com
   * histórico antigo.
   */
  const buildOverdueList = useCallback(
    async (cId: string): Promise<OverdueProcedureItem[]> => {
      const [pending, deadlines]: [Prescription[], OverdueDeadlinesConfig] = await Promise.all([
        PrescriptionService.listPendingProcedures(cId),
        OverdueDeadlinesService.load(cId),
      ]);
      if (pending.length === 0) return [];

      const overdueOnly = pending.filter((p) => {
        const threshold = p.type === 'SURGERY' ? deadlines.surgeryDays : deadlines.examDays;
        return daysSince(p.createdAt) >= threshold;
      });
      if (overdueOnly.length === 0) return [];

      const uniqueIds = Array.from(new Set(overdueOnly.map((p) => p.patientId)));
      const patients = await PatientService.getPatientsByIds(cId, uniqueIds);
      const nameById = new Map(patients.map((pt) => [pt.id, pt.name]));

      return overdueOnly
        .map((p): OverdueProcedureItem => {
          const threshold = p.type === 'SURGERY' ? deadlines.surgeryDays : deadlines.examDays;
          return {
            prescription: p,
            patientId: p.patientId,
            patientName: nameById.get(p.patientId) ?? 'Paciente removido',
            daysWaiting: daysSince(p.createdAt),
            thresholdDays: threshold,
          };
        })
        .sort((a, b) => b.daysWaiting - a.daysWaiting);
    },
    [],
  );

  // Snapshot listener — fila de aprovações é a única em tempo real porque é
  // a mais sensível (paciente acaba de pedir e quer ver "Agendada" rápido).
  useEffect(() => {
    if (!clinicId || !canSee(role, 'APPOINTMENT_APPROVAL')) {
      setAppointments([]);
      return;
    }
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('status', '==', 'PENDENTE'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Appointment[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Appointment, 'id'>),
        }));
        list.sort((a, b) => (a.date ?? 0) - (b.date ?? 0));
        setAppointments(list);
      },
      (err) => {
        console.error('[pendencias] appointments snapshot error:', err);
      },
    );
    return () => unsub();
  }, [clinicId, role]);

  // Snapshot listener — briefings pendentes também são em tempo real
  // porque o gatilho é "médico acabou de finalizar". Quanto antes a
  // secretária vir, antes consegue abordar o paciente que ainda está
  // saindo da consulta.
  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) {
      setBriefingsNeeded([]);
      return;
    }
    const unsub = SecretaryBriefingService.subscribePendingBriefings(
      clinicId,
      setBriefingsNeeded,
    );
    return () => unsub();
  }, [clinicId, role]);

  // Snapshot listener — cancelamentos feitos PELO PACIENTE no portal.
  //
  // Antes isto era completamente silencioso: a consulta virava CANCELADA,
  // sumia da agenda e ninguém na clínica ficava sabendo que a vaga tinha
  // vagado. Com poucas vagas online por dia, saber cedo é o que permite
  // reocupar o horário. O backend marca `portalCancelPending: true` no
  // cancelamento; a secretária dá "Ciente" e o item sai daqui.
  useEffect(() => {
    if (!clinicId || !canSee(role, 'PORTAL_CANCELLATION')) {
      setPortalCancellations([]);
      return;
    }
    const q = query(
      collection(db, 'clinics', clinicId, 'appointments'),
      where('portalCancelPending', '==', true),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Appointment[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Appointment, 'id'>),
        }));
        list.sort((a, b) => (a.date ?? 0) - (b.date ?? 0));
        setPortalCancellations(list);
      },
      (err) => {
        console.error('[pendencias] portal cancellations snapshot error:', err);
      },
    );
    return () => unsub();
  }, [clinicId, role]);

  // Snapshot listener — pacientes que se cadastraram sozinhos pelo portal.
  //
  // A fonte e a flag `portalReviewPending`, gravada SO pelo endpoint de
  // auto-cadastro. Nao use `registrationStatus === "MINIMAL"`: quase toda a
  // base migrada do sistema antigo e MINIMAL, entao a fila nasceria com
  // dezenas de milhares de itens e nunca esvaziaria.
  useEffect(() => {
    if (!clinicId || !canSee(role, 'PORTAL_NEW_PATIENT')) {
      setNewPortalPatients([]);
      return;
    }
    const q = query(
      collection(db, 'clinics', clinicId, 'patients'),
      where('portalReviewPending', '==', true),
      fsLimit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => d.data() as Patient);
        list.sort((a, b) => (b.portalSignupAt ?? 0) - (a.portalSignupAt ?? 0));
        setNewPortalPatients(list);
      },
      (err) => {
        console.error('[pendencias] novos pacientes do portal snapshot error:', err);
      },
    );
    return () => unsub();
  }, [clinicId, role]);

  const fetchOnce = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    setError(null);
    try {
      const tasks: Array<Promise<unknown>> = [];

      if (canSee(role, 'LENS_REQUEST')) {
        tasks.push(
          LensOrderRequestService.listPending(clinicId)
            .then(setLensRequests)
            .catch((err) => {
              console.error('[pendencias] lens requests failed:', err);
              setLensRequests([]);
            }),
        );
      } else {
        setLensRequests([]);
      }

      if (canSee(role, 'LENS_ORDER_IN_PROGRESS')) {
        tasks.push(
          LensOrderService.listOrders(clinicId, { max: 100 })
            .then((all) => setLensOrdersInProgress(all.filter((o) => o.status === 'CONFIRMED')))
            .catch((err) => {
              console.error('[pendencias] lens orders failed:', err);
              setLensOrdersInProgress([]);
            }),
        );
      } else {
        setLensOrdersInProgress([]);
      }

      if (canSee(role, 'STOCK_LOW')) {
        tasks.push(
          InventoryService.getStockAlerts(clinicId)
            .then(setStockAlerts)
            .catch((err) => {
              console.error('[pendencias] stock alerts failed:', err);
              setStockAlerts([]);
            }),
        );
      } else {
        setStockAlerts([]);
      }

      if (canSee(role, 'PROCEDURE_OVERDUE')) {
        tasks.push(
          buildOverdueList(clinicId)
            .then(setOverdueProcedures)
            .catch((err) => {
              console.error('[pendencias] overdue procedures failed:', err);
              setOverdueProcedures([]);
            }),
        );
      } else {
        setOverdueProcedures([]);
      }

      if (canSee(role, 'FINANCIAL_OPEN')) {
        tasks.push(
          ReceivableService.listOpen(clinicId)
            .then(setOpenReceivables)
            .catch((err) => {
              console.error('[pendencias] pagamentos em aberto failed:', err);
              setOpenReceivables([]);
            }),
        );
      } else {
        setOpenReceivables([]);
      }

      await Promise.all(tasks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar pendências.');
    } finally {
      setLoading(false);
    }
  }, [clinicId, role, buildOverdueList]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const financialPendencias = useMemo(
    () => openReceivables.filter((a) => isFinancialPendencia(a)),
    [openReceivables],
  );

  const total =
    appointments.length +
    lensRequests.length +
    lensOrdersInProgress.length +
    stockAlerts.length +
    overdueProcedures.length +
    briefingsNeeded.length +
    portalCancellations.length +
    newPortalPatients.length +
    financialPendencias.length;

  return {
    loading,
    error,
    appointments,
    lensRequests,
    lensOrdersInProgress,
    stockAlerts,
    overdueProcedures,
    briefingsNeeded,
    portalCancellations,
    newPortalPatients,
    openReceivables,
    financialPendencias,
    refresh: fetchOnce,
    total,
  };
}
